import { Polygon, PolygonProps } from "@react-google-maps/api"
import { differenceWith, isEqual, set } from "lodash-es"
import React, { useRef } from "react"
import { getDistance } from "utils/custom.utils"

interface StickyPolygonProps extends PolygonProps {
  boundaryPoints: any[]
  onChange: (a: any) => void
  disabled?: boolean
  /**
   * Set this toggle on in order to disable sticky points
   * @default false
   */
  disableStickyPoints: boolean
}
const StickyPolygon = ({
  boundaryPoints,
  path,
  onChange,
  disabled,
  disableStickyPoints = false,
  ...otherProps
}: StickyPolygonProps) => {
  const polyRef = useRef<any>()

  return (
    <Polygon
      {...otherProps}
      onMouseUp={(e: any) => {
        const polygonPath = polyRef.current?.getPath()
        const coords: any[] = polygonPath
          ?.getArray()
          ?.map((a: any) => a.toJSON())

        const difference = differenceWith(coords, path as any, isEqual)

        // Only run the computations if there are differences in the polygon and sticky points are enabled
        if (difference.length > 0) {
          // Only run the algorithm if stickyPoints are not disabled
          if (!disableStickyPoints) {
            for (const different of difference ?? []) {
              const idx = coords.indexOf(different)
              for (const parentCoord of boundaryPoints ?? []) {
                const distance = getDistance(parentCoord, different)
                if (distance && distance < 300) {
                  if (idx === 0) {
                    set(coords, coords.length - 1, parentCoord)
                  } else if (idx === coords.length - 1) {
                    set(coords, 0, parentCoord)
                  }
                  set(coords, idx, parentCoord)
                }
              }
            }
          }
          set(coords, 0, coords[coords.length - 1])
          onChange(coords)
        } else {
          // Only proceed with the delete operation if the vertex exists already (on the polygon)
          if (e.vertex) {
            const isLastVertex = e.vertex === coords.length - 1
            // If there are no changes in the polygon then remove the point which the user has clicked on
            const filteredCoords = coords.filter((_, idx) => {
              if (isLastVertex) {
                // If the selected vertex is the last vertex then remove it along with the first vertex
                return idx !== e.vertex && idx !== 0
              } else {
                return idx !== e.vertex
              }
            })

            // Replace the last point with the first point to complete the polygon
            set(filteredCoords, filteredCoords.length - 1, filteredCoords[0])
            onChange(filteredCoords)
          }
        }
      }}
      path={path}
      editable={disabled ? false : true}
      onLoad={poly => {
        polyRef.current = poly
      }}
    />
  )
}

export default StickyPolygon
